import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, CheckSquare, Square, Send, Loader2, Users, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";

// Coach → Attendance: team checkbox grid → +25 pts each present → report emailed to the DOC.
export default function AttendanceGrid({ token }) {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [groups, setGroups] = useState([]);
  const [gid, setGid] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [present, setPresent] = useState(new Set());
  const [docEmail, setDocEmail] = useState("");
  const [sendReport, setSendReport] = useState(true);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => {
    axios.get(`${API}/coach/attendance/groups`, auth).then(({ data }) => { setGroups(data); if (!gid && data[0]) setGid(`${data[0].group_type}:${data[0].group_id}`); }).catch(() => toast.error("Couldn't load teams."));
    axios.get(`${API}/coach/attendance`, auth).then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(load, [auth]); // eslint-disable-line react-hooks/exhaustive-deps
  const group = groups.find((g) => `${g.group_type}:${g.group_id}` === gid);
  useEffect(() => { setPresent(new Set()); setDocEmail(group?.doc_email || ""); }, [gid, group?.doc_email]);

  const toggle = (id) => setPresent((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const all = () => setPresent(new Set(group.players.map((p) => p.id)));

  const save = async () => {
    if (!group) return;
    setBusy(true);
    try {
      const present_ids = [...present];
      const absent_ids = group.players.filter((p) => !present.has(p.id)).map((p) => p.id);
      const { data } = await axios.post(`${API}/coach/attendance`, { group_type: group.group_type, group_id: group.group_id, group_name: group.group_name, date, present_ids, absent_ids, doc_email: docEmail, send_report: sendReport, points: 25 }, auth);
      toast.success(`Attendance saved · ${data.present} present · +${data.points_each} pts each`, { description: data.reported ? `Report emailed to ${docEmail}` : sendReport && docEmail ? "Report could not be emailed" : "No DOC report sent" });
      setPresent(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save attendance."); }
    finally { setBusy(false); }
  };

  const input = "bg-[#141414] border border-white/10 rounded-md p-2.5 text-white text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#D4AF37]";
  return (
    <div data-testid="attendance-grid" className="space-y-6">
      <div className="rounded-lg border border-white/10 bg-[#0d0d0d] p-5">
        <div className="flex items-center gap-2 mb-1"><ClipboardCheck size={16} style={{ color: GOLD }} /><h2 className="font-subheadings text-sm font-bold uppercase tracking-widest">Group Attendance</h2></div>
        <p className="text-xs text-white/50 mb-4">Check who showed up. Save awards <b className="text-[#D4AF37]">+25 Hub points</b> per present player and emails a present/absent report to the team's Director of Coaching.</p>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <select data-testid="attendance-group-select" value={gid} onChange={(e) => setGid(e.target.value)} className={input}>
            {groups.length === 0 && <option value="">No teams with players yet</option>}
            {groups.map((g) => <option key={`${g.group_type}:${g.group_id}`} value={`${g.group_type}:${g.group_id}`}>{g.group_name} ({g.players.length})</option>)}
          </select>
          <input data-testid="attendance-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} />
          <div className="flex items-center gap-2">
            <Mail size={14} className="text-white/40 shrink-0" />
            <input data-testid="attendance-doc-email" type="email" value={docEmail} onChange={(e) => setDocEmail(e.target.value)} placeholder="DOC email for the report" className={`${input} flex-1`} />
          </div>
        </div>
        {group && (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 flex items-center gap-1.5"><Users size={11} /> {group.players.length} players · {present.size} present</span>
              <button data-testid="attendance-mark-all" onClick={all} className="text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] hover:underline">Mark all present</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {group.players.map((p) => {
                const on = present.has(p.id);
                return (
                  <button key={p.id} data-testid={`attendance-player-${p.id}`} onClick={() => toggle(p.id)} aria-pressed={on}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-left text-sm transition-colors ${on ? "border-[#22C55E]/60 bg-[#22C55E]/10 text-white" : "border-white/10 bg-[#141414] text-white/70 hover:border-white/25"}`}>
                    {on ? <CheckSquare size={16} className="text-[#22C55E] shrink-0" /> : <Square size={16} className="text-white/30 shrink-0" />}
                    <span className="truncate">{p.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
                <input data-testid="attendance-send-report" type="checkbox" checked={sendReport} onChange={(e) => setSendReport(e.target.checked)} className="accent-[#D4AF37]" /> Email report to DOC
              </label>
              <button data-testid="attendance-save-btn" onClick={save} disabled={busy || present.size === 0} className="ml-auto inline-flex items-center gap-2 text-black text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm disabled:opacity-50" style={{ background: GOLD }}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Save & Award Points
              </button>
            </div>
          </>
        )}
      </div>
      {history.length > 0 && (
        <div data-testid="attendance-history" className="rounded-lg border border-white/10 bg-[#0d0d0d] p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-3">Recent sessions</h3>
          <div className="space-y-2">
            {history.slice(0, 8).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
                <span className="text-white/80 truncate">{h.date} · {h.group_name}</span>
                <span className="text-white/50 whitespace-nowrap ml-3">{h.present.length} present · {h.absent.length} absent {h.reported && <span className="text-[#22C55E]">· reported</span>}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
